import React, { useState, useEffect } from 'react';
import { X, Download, Check, Image as ImageIcon, Film, Clock } from 'lucide-react';
import './StoryDownloader.css';

const StoryDownloader = ({ user: initialUser, onClose }) => {
  const [user, setUser] = useState(initialUser);
  const [archiveItems, setArchiveItems] = useState([]);
  const [activeHighlightId, setActiveHighlightId] = useState(null); // null = Recent, 'history' = Local Archive
  const [isGridLoading, setIsGridLoading] = useState(false);
  const [fullInfo, setFullInfo] = useState(null);
  
  const [downloading, setDownloading] = useState({});
  const [downloaded, setDownloaded] = useState({});
  const [selectedPreview, setSelectedPreview] = useState(null);

  useEffect(() => {
    fetchFullInfo();
  }, [user.id]);

  const fetchFullInfo = async () => {
    try {
        const resp = await fetch(`/api/user-info/${user.id}`);
        const data = await resp.json();
        setFullInfo(data);
    } catch(e) { /* tant pis */ }
  };



  const handleHighlightClick = async (hid) => {
    setActiveHighlightId(hid);
    if (!hid) return; // Retour aux stories récentes

    if (hid === 'history') {
        setIsGridLoading(true);
        try {
            const resp = await fetch(`/api/archive/${user.id}`);
            const data = await resp.json();
            // Transformer les données de l'archive locale pour être compatible avec la grille
            const formatted = (data || []).map(item => ({
                id: item.id,
                type: item.type,
                url: item.proxy_url || item.local_url,
                preview: item.proxy_url || item.local_url,
                timestamp: item.timestamp,
                isLocal: true,
                original_url: item.url
            }));
            setArchiveItems(formatted);
        } catch (e) {
            console.error("Erreur items archive", e);
        }
        setIsGridLoading(false);
        return;
    }


  };

  const handleDownload = async (story) => {
    // Si c'est déjà local, le téléchargement est "instantané" ou inutile, 
    // mais on garde la logique pour la cohérence
    if (story.isLocal) {
        setDownloaded(prev => ({ ...prev, [story.id]: true }));
        return;
    }
    setDownloading(prev => ({ ...prev, [story.id]: true }));
    try {
      const response = await fetch('/api/download-to-disk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: story.original_url || story.url,
          username: user.username,
          story_id: story.id,
          type: story.type
        })
      });
      
      const result = await response.json();
      if (result.success) {
        setDownloaded(prev => ({ ...prev, [story.id]: true }));
        setTimeout(() => {
          setDownloaded(prev => ({ ...prev, [story.id]: false }));
        }, 3000);
      }
    } catch (error) {
      console.error("Erreur de téléchargement", error);
    }
    setDownloading(prev => ({ ...prev, [story.id]: false }));
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const diff = Math.floor(Date.now() / 1000) - timestamp;
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  // On affiche les stories du cache si un highlight est actif, sinon les stories récentes
  const getDisplayedStories = () => {
    if (activeHighlightId === 'history') return archiveItems;
    return user.stories || [];
  };

  const displayedStories = getDisplayedStories();

  return (
    <div className="profile-page fade-in">
      {/* Barre de navigation supérieure (Style App) */}
      <div className="profile-navbar glass-effect">
        <button className="back-btn" onClick={onClose}>
          <X size={20} />
          <span>Retour</span>
        </button>
        <span className="navbar-username">{user.username}</span>
        <div style={{width: 60}}></div> {/* Équilibre visuel */}
      </div>

      <div className="profile-content">
        {/* En-tête Profil (Style Instagram) */}
        <header className="profile-header">
          <div className="profile-avatar-section">
            <div className={`profile-avatar-ring ${user.stories?.length > 0 ? 'active' : ''}`}>
              <img src={user.avatar} alt="avatar" className="profile-avatar" />
            </div>
          </div>
          
          <div className="profile-info-section">
            <div className="profile-actions-row">
              <h2 className="profile-username">{user.username}</h2>
              <button className="download-all-btn">S'abonner</button>
            </div>

            <div className="profile-stats-row">
              <div className="stat-item"><strong>{displayedStories.length}</strong> stories</div>
              <div className="stat-item"><strong>{fullInfo?.follower_count?.toLocaleString() || '0'}</strong> abonnés</div>
              <div className="stat-item"><strong>{fullInfo?.following_count?.toLocaleString() || '0'}</strong> abonnements</div>
            </div>

            <div className="profile-bio-section">
              <h1 className="profile-full-name">{fullInfo?.full_name || user.username}</h1>
              {fullInfo?.biography && <p className="profile-bio">{fullInfo.biography}</p>}
              {fullInfo?.external_url && (
                <a href={fullInfo.external_url} target="_blank" rel="noopener noreferrer" className="bio-link">
                  {fullInfo.external_url.replace('https://', '').replace('www.', '').split('/')[0]}
                </a>
              )}
            </div>
          </div>
        </header>

        {/* Barre des Onglets (Highlights + Recents + Archive) */}
        <div className="profile-tabs-bar">
          <div className="tabs-container">
            <div 
              className={`tab-item ${!activeHighlightId ? 'active' : ''}`}
              onClick={() => handleHighlightClick(null)}
            >
              <span>RÉCENT</span>
            </div>

            <div 
              className={`tab-item ${activeHighlightId === 'history' ? 'active' : ''}`}
              onClick={() => handleHighlightClick('history')}
            >
              <Clock size={14} />
              <span>HISTORIQUE</span>
            </div>


          </div>
        </div>

        {/* Grille de Stories (Style Instagram - Carré 3x3) */}
        <div className="instagram-grid">
          {isGridLoading ? (
             <div className="grid-loading">Chargement de la bibliothèque...</div>
          ) : displayedStories.length === 0 ? (
            <div className="no-content">
              <ImageIcon size={48} opacity={0.3} />
              <p>Aucune story disponible ici.</p>
            </div>
          ) : (
            displayedStories.map((story) => (
              <div key={story.id} className="grid-item" onClick={() => setSelectedPreview(story)}>
                <div className="grid-media-wrapper">
                  {story.type === 'video' ? (
                    <video 
                      src={story.url} 
                      className="grid-media" 
                      muted 
                      loop 
                      onMouseEnter={e => e.target.play()} 
                      onMouseLeave={e => e.target.pause()} 
                    />
                  ) : (
                    <img src={story.url} alt="preview" className="grid-media" />
                  )}
                  
                  <div className="grid-item-overlay">
                    <div className="overlay-info">
                       {story.type === 'video' ? <Film size={18} /> : <ImageIcon size={18} />}
                       <span className="overlay-time">{formatTimeAgo(story.isLocal ? story.timestamp : story.taken_at)}</span>
                    </div>
                    <button 
                      className={`grid-download-btn ${downloaded[story.id] ? 'success' : ''}`}
                      onClick={(e) => { e.stopPropagation(); handleDownload(story); }}
                      disabled={downloading[story.id]}
                    >
                      {downloading[story.id] ? <div className="loader-mini"></div> : <Download size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        
        <footer className="profile-footer">
            <p>L'historique des stories est conservé dans le fichier <code>archive_metadata.json</code> de l'utilisateur.</p>
        </footer>
      </div>

      {/* Lightbox / Zoom Overlay */}
      {selectedPreview && (
        <div className="lightbox-overlay fade-in" onClick={(e) => { e.stopPropagation(); setSelectedPreview(null); }}>
          <button className="lightbox-close" onClick={() => setSelectedPreview(null)}><X size={32} /></button>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            {selectedPreview.type === 'video' ? (
              <video src={selectedPreview.url} autoPlay controls className="lightbox-media" />
            ) : (
              <img src={selectedPreview.url} alt="zoom" className="lightbox-media" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryDownloader;
